import type { Worksheet } from "exceljs";
import type { Department } from "../../../contexts/AuthProvider";
import { type DepartmentLocation, type NurseInShift, type PrintoutSchedule, StatsMode } from "../../../utils/models";
import { getRoleGroupsForRole } from "../../../utils/roles";
import { filterNursesForStats } from "../../../utils/statsCalculations";
import { mergeAcrossRow } from "./rowMerge";

export interface RoleGroupStat {
	groupName: string;
	amount: number;
	ratio: number;
}

interface CalculateArgs {
	nurses: NurseInShift[];
	/** All day-active config locations, each carrying its census-resolved `amountRequired`. */
	departmentLocations: DepartmentLocation[];
	department: Department;
	hospitalData: PrintoutSchedule;
}

/**
 * Compute the role-group stats for a section (mapping §9.2).
 *
 * - **Assigned** = the section's nurses counted per role group (includes nurses with no location).
 * - **Required** = each role group's department-wide requirement: the sum of every config location's
 *   census-resolved `amountRequired` (already computed by `applyRatioLadderToLocations` via the
 *   shared resolver) for the role groups it serves. This matches the daily view's aggregate.
 *
 * Only role groups present in the section's nurses are emitted, so a role/job-grouped section shows
 * just its own group(s) rather than every department group.
 */
export const calculateRoleGroupStats = (args: CalculateArgs): RoleGroupStat[] => {
	const { nurses, departmentLocations, department, hospitalData } = args;
	if (department.statsMode !== StatsMode.Ratio || !department.role_groups) return [];

	const filteredNurses = nurses.filter((n) => filterNursesForStats(n, false, hospitalData.nurses, false, false));

	const roleGroupCounts: Record<string, number> = {};
	for (const nurse of filteredNurses) {
		const fullNurse = hospitalData.nurses.find((n) => n.id === nurse.nurseId);
		if (!fullNurse) continue;
		for (const roleGroup of getRoleGroupsForRole(fullNurse.role, department)) {
			roleGroupCounts[roleGroup] = (roleGroupCounts[roleGroup] || 0) + 1;
		}
	}

	const roleGroupRatios: Record<string, number> = {};
	for (const location of departmentLocations) {
		const servedRoleGroups = new Set<string>();
		for (const role of location.roles) {
			if (role === "all") {
				for (const groupName of Object.keys(department.role_groups)) servedRoleGroups.add(groupName);
			} else {
				for (const groupName of getRoleGroupsForRole(role, department)) servedRoleGroups.add(groupName);
			}
		}
		for (const groupName of servedRoleGroups) roleGroupRatios[groupName] = (roleGroupRatios[groupName] || 0) + (location.amountRequired || 0);
	}

	return Object.keys(roleGroupCounts)
		.filter((rg) => roleGroupCounts[rg] > 0)
		.map<RoleGroupStat>((rg) => ({ groupName: rg, amount: roleGroupCounts[rg], ratio: roleGroupRatios[rg] || 0 }));
};

interface CalculateJobStatsArgs {
	nurses: NurseInShift[];
	/** All day-active config locations, each carrying its census-resolved `amountRequired`. */
	departmentLocations: DepartmentLocation[];
	department: Department;
}

/**
 * JobManagement equivalent of `calculateRoleGroupStats` — groups by `jobId`.
 *
 * - **Assigned** = the section's nurses counted per job (includes nurses with no location).
 * - **Required** = each job's department-wide requirement: the sum of every config location's
 *   census-resolved `amountRequired` keyed by `location.jobId` (matches the daily view's aggregate).
 *
 * Only jobs present in the section's nurses are emitted, so a job-grouped section shows just its own
 * job instead of every department job.
 */
export const calculateJobGroupStats = (args: CalculateJobStatsArgs): RoleGroupStat[] => {
	const { nurses, departmentLocations, department } = args;
	if (department.statsMode !== StatsMode.Ratio || !department.jobs?.length) return [];

	const jobCounts: Record<string, number> = {};
	for (const nurse of nurses) {
		if (!nurse.jobId) continue;
		jobCounts[nurse.jobId] = (jobCounts[nurse.jobId] || 0) + 1;
	}

	const jobRatios: Record<string, number> = {};
	for (const location of departmentLocations) {
		if (!location.jobId) continue;
		jobRatios[location.jobId] = (jobRatios[location.jobId] || 0) + (location.amountRequired || 0);
	}

	const displayName = (jobId: string) => department.jobs?.find((j) => j.id === jobId)?.displayName ?? jobId;
	return Object.keys(jobCounts).map<RoleGroupStat>((jobId) => ({
		groupName: displayName(jobId),
		amount: jobCounts[jobId],
		ratio: jobRatios[jobId] || 0,
	}));
};

interface EmitStatsArgs {
	worksheet: Worksheet;
	currentRow: number;
	stats: RoleGroupStat[];
	totalColumns: number;
}

/**
 * Render the per-section stats rows under the section title.
 *
 * One row per stat, each centered, italic text:  `<groupName>: Total Assigned <N>. Total Required: <M>`.
 * Returns the row number after the emitted stats.
 */
export const emitRoleGroupStatRows = ({ worksheet, currentRow: initialRow, stats, totalColumns }: EmitStatsArgs): number => {
	let currentRow = initialRow;
	for (const stat of stats) {
		const cell = worksheet.getCell(`A${currentRow}`);
		cell.value = `${stat.groupName}: Total Assigned ${stat.amount}. Total Required: ${stat.ratio}`;
		cell.font = { bold: false, size: 11, color: { argb: "FF666666" } };
		cell.alignment = { horizontal: "center" };
		mergeAcrossRow(worksheet, currentRow, totalColumns);
		currentRow += 1;
	}
	return currentRow;
};
